import React from "react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip } from "recharts";
import { RHYTHM_OPTIONS, followsRhythm, streamMonth } from "@/lib/incomeHelpers";
import { monthNames } from "@/lib/costHelpers";
import { formatGBP } from "@/lib/brand";

export const RhythmStep = ({ inputs, setPreset, setNote, toggleStream }) => {
  const preset = inputs.seasonality_preset || "steady";
  const excluded = inputs.seasonality_excluded || [];
  const streams = inputs.revenue_streams.filter((r) => r.name);
  const labels = monthNames(inputs.start_month);
  const data = labels.map((m, i) => ({
    m,
    total: Math.round(streams.reduce((s, r) => s + streamMonth(r, i, inputs.start_month, preset, excluded), 0)),
  }));
  return (
    <div className="space-y-6" data-testid="rhythm-step">
      <p className="text-black/80 leading-relaxed">
        Do you have busier and quieter months? Pick what feels closest and watch your income change below. You'll discover your real rhythm once you're logging actuals.
      </p>
      <div className="card" data-testid="rhythm-preview">
        <div className="section-eyebrow mb-3">Your income, month by month</div>
        {streams.length === 0 ? (
          <p className="text-sm text-black/60">Add an income stream in the previous step to see your preview.</p>
        ) : (
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <XAxis dataKey="m" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 11 }} width={60} tickFormatter={(v) => formatGBP(v)} />
                <Tooltip formatter={(v) => formatGBP(v)} />
                <Bar dataKey="total" name="Income" fill="#FFBD4A" stroke="#000" strokeWidth={1.5} radius={[6, 6, 0, 0]} isAnimationActive />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        {RHYTHM_OPTIONS.map((opt) => (
          <button
            type="button"
            key={opt.k}
            data-testid={`seasonality-${opt.k}`}
            onClick={() => setPreset(opt.k)}
            className={`text-left p-4 rounded-xl border-[1.5px] transition-colors ${preset === opt.k ? "bg-[#FFBD4A] border-black" : "bg-white border-black/55 hover:bg-[#FFF7E5]"}`}
          >
            <div className="font-heading uppercase">{opt.label}</div>
            <div className="text-sm text-black/70">{opt.body}</div>
          </button>
        ))}
      </div>
      {preset !== "steady" && preset !== "custom" && streams.length > 0 && (
        <div className="card" data-testid="rhythm-streams">
          <div className="section-eyebrow mb-3">Which income streams follow this rhythm?</div>
          <div className="space-y-2">
            {streams.map((r) =>
              followsRhythm(r) ? (
                <label key={r.id} className="flex items-center gap-3 text-sm">
                  <input type="checkbox" checked={!excluded.includes(r.id)} onChange={() => toggleStream(r.id)} data-testid={`rhythm-stream-${r.id}`} />
                  {r.name}
                </label>
              ) : (
                <div key={r.id} className="text-sm text-black/55">{r.name}: already set month by month</div>
              )
            )}
          </div>
        </div>
      )}
      {preset === "custom" && (
        <div>
          <label className="block text-xs font-semibold mb-1">Describe your rhythm in your own words</label>
          <textarea className="input min-h-[90px]" value={inputs.seasonality_note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Busy in Q4 and Q1, slower Q2–Q3." data-testid="seasonality-note" maxLength={500} />
          <p className="text-xs text-black/55 mt-2">Your AI coach will use this. For precise control, use Custom months &amp; sales on an income stream.</p>
        </div>
      )}
    </div>
  );
};

export default RhythmStep;
